import { useEffect, useRef, useState } from "react";
import { BlobField } from "./BlobField";
import { openingPlan, isSwipeUp } from "../shared/opening";
import { buzz } from "../lib/haptics";

// The opening ritual.
//
// Not a splash screen and not a loading state: the app opens on a sheet of
// glass with the wordmark etched into it, and you swipe up to come in. The
// glass lifts and the lit home is underneath, which is the whole metaphor in
// one gesture. It runs once per app-open, and it gets out of the way fast on
// every open after the first.

const SESSION_KEY = "opening_shown";
const SEEN_KEY = "opening_seen_before";

export function hasOpenedThisSession(): boolean {
  try { return sessionStorage.getItem(SESSION_KEY) === "1"; } catch { return false; }
}

export function Opening({ onDone }: { onDone: () => void }) {
  const [openedBefore] = useState(() => {
    try { return localStorage.getItem(SEEN_KEY) === "1"; } catch { return false; }
  });
  const plan = openingPlan(openedBefore);
  const [leaving, setLeaving] = useState(false);
  const startY = useRef<number | null>(null);
  const left = useRef(false);

  function leave() {
    if (left.current) return;
    left.current = true;
    buzz("commit");
    setLeaving(true);
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
      localStorage.setItem(SEEN_KEY, "1");
    } catch { /* private mode: the ritual just runs again */ }
    // Let the glass finish lifting before the screen underneath takes over.
    window.setTimeout(onDone, 820);
  }

  useEffect(() => {
    if (plan.autoLiftMs === null) return;
    const t = window.setTimeout(leave, plan.autoLiftMs);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`opening${leaving ? " gone" : ""}`}
      onPointerDown={(e) => { startY.current = e.clientY; }}
      onPointerUp={(e) => {
        if (startY.current !== null && isSwipeUp(startY.current, e.clientY)) leave();
        startY.current = null;
      }}
      // Tapping works too. Somebody who does not read the hint should still
      // get in.
      onClick={leave}
    >
      <BlobField count={6} />
      <div className="op-pane">
        <div
          className="op-mark"
          style={{ animationDuration: `${plan.inscribeMs}ms` }}
        >
          <span className="op-word">How Y&apos;all Been</span>
        </div>
        {plan.showTagline && <p className="op-tag">keeping up with your people</p>}
        <div className="op-hint" style={{ animationDelay: `${plan.hintAtMs}ms` }}>
          <span className="op-arrow" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m6 15 6-6 6 6" />
            </svg>
          </span>
          <span>swipe up to come in</span>
        </div>
      </div>
    </div>
  );
}
